import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Slider from "@mui/material/Slider";
import Typography from "@mui/material/Typography";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { useTheme } from "@mui/material/styles";

interface AudioPlayerProps {
    src: string;
    duration: number;
    currentTime?: number;
    onTimeUpdate?: (time: number) => void;
    onEnded?: () => void;
    onSeek?: (time: number) => void;
    autoPlay?: boolean;
    onPlay?: () => void;
    onPause?: () => void;
}

export default function AudioPlayer({
    src,
    duration,
    currentTime: externalCurrentTime,
    onTimeUpdate,
    onEnded,
    onSeek,
    autoPlay = false,
    onPlay,
    onPause,
}: AudioPlayerProps) {
    console.log("[AudioPlayer] Component render", { src, duration, autoPlay });
    const audioRef = useRef<HTMLAudioElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [actualDuration, setActualDuration] = useState(duration); // Use actual audio duration when available
    const theme = useTheme();

    // Update audio src when it changes
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) {
            console.log("[AudioPlayer] src useEffect: audio ref not available");
            return;
        }

        console.log("[AudioPlayer] src useEffect: src changed to", src);

        if (src) {
            // Pause current playback if any
            const wasPlaying = !audio.paused;
            console.log("[AudioPlayer] src useEffect: wasPlaying", wasPlaying);
            audio.pause();
            setIsPlaying(false);
            // Update src and reload
            audio.src = src;
            console.log("[AudioPlayer] src useEffect: Set audio.src to", src);
            audio.load();
            console.log("[AudioPlayer] src useEffect: Called audio.load()");
            setCurrentTime(0);
            // Reset to prop duration until actual duration is loaded from audio file
            setActualDuration(duration);
        } else {
            console.log("[AudioPlayer] src useEffect: No src, clearing");
            audio.src = "";
            setCurrentTime(0);
            setIsPlaying(false);
        }
    }, [src, duration]);

    // Handle external currentTime updates
    useEffect(() => {
        if (audioRef.current && externalCurrentTime !== undefined) {
            if (
                Math.abs(audioRef.current.currentTime - externalCurrentTime) > 1
            ) {
                audioRef.current.currentTime = externalCurrentTime;
                setCurrentTime(externalCurrentTime);
            }
        }
    }, [externalCurrentTime]);

    // Set up event listeners
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) {
            console.log(
                "[AudioPlayer] event listeners useEffect: audio ref not available",
            );
            return;
        }

        console.log(
            "[AudioPlayer] event listeners useEffect: Setting up event listeners",
        );

        const handleTimeUpdate = () => {
            const newTime = audio.currentTime;
            setCurrentTime(newTime);
            onTimeUpdate?.(newTime);
        };

        const handleEnded = () => {
            console.log("[AudioPlayer] handleEnded: Audio ended");
            setIsPlaying(false);
            onEnded?.();
        };

        const handlePlay = () => {
            console.log(
                "[AudioPlayer] handlePlay: Play event fired, currentTime:",
                audio.currentTime,
            );
            setIsPlaying(true);
            onPlay?.();
        };

        const handlePause = () => {
            console.log("[AudioPlayer] handlePause: Pause event fired");
            setIsPlaying(false);
            onPause?.();
        };

        const handleError = (e: Event) => {
            const error = e.target as HTMLAudioElement;
            console.error("[AudioPlayer] handleError: Audio error occurred", {
                error: error.error,
                networkState: error.networkState,
                readyState: error.readyState,
                src: error.src,
            });
            setIsPlaying(false);
        };

        const handleCanPlay = () => {
            console.log(
                "[AudioPlayer] handleCanPlay: Audio can play, readyState:",
                audio.readyState,
            );
        };

        const handleLoadedMetadata = () => {
            console.log(
                "[AudioPlayer] handleLoadedMetadata: Metadata loaded, duration:",
                audio.duration,
            );
            // Use the actual duration from the audio file if it's valid
            if (
                audio.duration &&
                isFinite(audio.duration) &&
                audio.duration > 0
            ) {
                console.log(
                    "[AudioPlayer] Updating actual duration from",
                    actualDuration,
                    "to",
                    audio.duration,
                );
                setActualDuration(audio.duration);
            }
        };

        const handleLoadStart = () => {
            console.log("[AudioPlayer] handleLoadStart: Load started");
        };

        audio.addEventListener("timeupdate", handleTimeUpdate);
        audio.addEventListener("ended", handleEnded);
        audio.addEventListener("play", handlePlay);
        audio.addEventListener("pause", handlePause);
        audio.addEventListener("error", handleError);
        audio.addEventListener("canplay", handleCanPlay);
        audio.addEventListener("loadedmetadata", handleLoadedMetadata);
        audio.addEventListener("loadstart", handleLoadStart);

        // Set volume to full (1.0)
        audio.volume = 1.0;

        return () => {
            console.log(
                "[AudioPlayer] event listeners useEffect: Cleaning up event listeners",
            );
            audio.removeEventListener("timeupdate", handleTimeUpdate);
            audio.removeEventListener("ended", handleEnded);
            audio.removeEventListener("play", handlePlay);
            audio.removeEventListener("pause", handlePause);
            audio.removeEventListener("error", handleError);
            audio.removeEventListener("canplay", handleCanPlay);
            audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
            audio.removeEventListener("loadstart", handleLoadStart);
        };
    }, [onTimeUpdate, onEnded, onPlay, onPause, actualDuration]);

    // Handle autoplay separately to avoid re-registering listeners
    useEffect(() => {
        if (autoPlay && src && audioRef.current) {
            console.log(
                "[AudioPlayer] autoplay useEffect: Attempting autoplay",
            );
            audioRef.current.play().catch((err) => {
                console.error(
                    "[AudioPlayer] autoplay useEffect: Autoplay failed:",
                    err,
                );
            });
        }
    }, [autoPlay, src]);

    const togglePlayPause = async () => {
        console.log(
            "[AudioPlayer] togglePlayPause: Called, isPlaying:",
            isPlaying,
        );
        const audio = audioRef.current;
        if (!audio) {
            console.warn(
                "[AudioPlayer] togglePlayPause: audio ref not available",
            );
            return;
        }
        if (!src) {
            console.warn(
                "[AudioPlayer] togglePlayPause: src not available, current src:",
                audio.src,
            );
            return;
        }

        console.log("[AudioPlayer] togglePlayPause: Audio state", {
            src: audio.src,
            readyState: audio.readyState,
            networkState: audio.networkState,
            paused: audio.paused,
            currentTime: audio.currentTime,
            duration: audio.duration,
        });

        try {
            if (isPlaying) {
                console.log("[AudioPlayer] togglePlayPause: Pausing audio");
                audio.pause();
            } else {
                console.log(
                    "[AudioPlayer] togglePlayPause: Attempting to play",
                );
                // Ensure audio is loaded before playing
                if (audio.readyState < 2) {
                    console.log(
                        "[AudioPlayer] togglePlayPause: readyState < 2, calling load()",
                    );
                    audio.load();
                }
                console.log(
                    "[AudioPlayer] togglePlayPause: Calling audio.play()",
                );
                await audio.play();
                console.log(
                    "[AudioPlayer] togglePlayPause: audio.play() succeeded",
                );
            }
        } catch (error) {
            console.error(
                "[AudioPlayer] togglePlayPause: Error playing audio:",
                error,
            );
            setIsPlaying(false);
        }
    };

    const handleSeek = (_event: Event, newValue: number | number[]) => {
        const seekTime = newValue as number;
        if (audioRef.current) {
            audioRef.current.currentTime = seekTime;
            setCurrentTime(seekTime);
            // Notify parent of seek if handler provided
            if (onSeek) {
                onSeek(seekTime);
            }
        }
    };

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                p: 2,
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <audio ref={audioRef} preload="metadata" crossOrigin="anonymous" />

            {/* Controls */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <IconButton
                    onClick={(e) => {
                        console.log(
                            "[AudioPlayer] IconButton onClick fired",
                            e,
                        );
                        togglePlayPause();
                    }}
                    color="primary"
                    size="large"
                    sx={{
                        width: 56,
                        height: 56,
                    }}
                >
                    {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>

                <Box sx={{ flexGrow: 1 }}>
                    <Slider
                        value={currentTime}
                        min={0}
                        max={actualDuration || 100}
                        onChange={handleSeek}
                        sx={{
                            "& .MuiSlider-thumb": {
                                width: 16,
                                height: 16,
                            },
                        }}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 0.5,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {formatTime(currentTime)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatTime(actualDuration)}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
